import { useEffect, useRef } from 'react';
import './ErrorBoundary.css';

/**
 * CatastrophicErrorUI Component (Story 6.3 Task 5)
 *
 * Displayed when demo assets cannot be loaded (catastrophic failure).
 * Shows a text-only error message with a "Refresh Page" button.
 *
 * Unlike ErrorUI, this component does NOT offer a "Play Demo" option
 * because the demo assets themselves have failed to load.
 *
 * @component
 * @example
 * ```tsx
 * <CatastrophicErrorUI />
 * ```
 */
const CatastrophicErrorUI = () => {
  const refreshButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-focus Refresh Page button on mount
  useEffect(() => {
    refreshButtonRef.current?.focus();
  }, []);

  // Escape key handler: Refreshes the page (same as clicking Refresh Page)
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        window.location.reload();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="error-ui-container error-boundary-container" role="alert" aria-live="assertive">
      {/* Screen reader announcement - visually hidden but announced */}
      <div className="sr-only" aria-live="assertive">
        Alert: Unable to load game. Demo assets could not be loaded. Press Refresh Page button to retry.
      </div>

      <div className="error-boundary-content">
        <h1 className="error-heading">Unable to load game</h1>
        <p className="error-message">
          Demo assets could not be loaded. Please refresh the page.
        </p>

        <button
          ref={refreshButtonRef}
          className="refresh-button"
          onClick={handleRefresh}
          aria-label="Refresh page"
        >
          {/* Refresh icon SVG - 20px */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{ marginRight: '8px', verticalAlign: 'middle' }}
            aria-hidden="true"
          >
            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
          </svg>
          Refresh Page
        </button>
      </div>
    </div>
  );
};

export default CatastrophicErrorUI;
